import type { WithdrawalFxQuote } from '@/services/wallet.service';

export type WalletCurrency = 'BRL' | 'USD';
export type WithdrawalMode = 'original' | 'convert';

export const WITHDRAWAL_FX_REFERENCE_AMOUNT: Record<WalletCurrency, number> = {
  BRL: 10000,
  USD: 5000,
};

export function computeWithdrawalFxFromReference(
  amountInCents: number,
  referenceQuote: WithdrawalFxQuote,
): WithdrawalFxQuote | null {
  if (amountInCents <= 0 || !referenceQuote.amount_source) return null;

  return {
    ...referenceQuote,
    amount_source: amountInCents,
    amount_converted: Math.round(
      (amountInCents * referenceQuote.amount_converted) / referenceQuote.amount_source,
    ),
  };
}

export function normalizeWalletCurrency(currency: string): WalletCurrency {
  return currency === 'USD' ? 'USD' : 'BRL';
}

export function getTargetCurrency(walletCurrency: WalletCurrency): WalletCurrency {
  return walletCurrency === 'BRL' ? 'USD' : 'BRL';
}

export function isFxPairSupported(
  supportedPairs: { from: string; to: string }[] | undefined,
  from: WalletCurrency,
  to: WalletCurrency,
): boolean {
  if (!supportedPairs?.length) return true;
  return supportedPairs.some((p) => p.from === from && p.to === to);
}

/**
 * Moeda de origem do saque = filtro global (CurrencyToggle). A API de referral/partner
 * já responde por `?currency=`, então a origem acompanha a wallet filtrada. Para receber
 * na outra moeda usa-se payout_currency (modo convert), não esta função.
 */
export function resolveWithdrawalSourceCurrency(
  _withdrawalType: string | undefined,
  globalFilterCurrency: string,
): WalletCurrency {
  return normalizeWalletCurrency(globalFilterCurrency);
}

export function resolveEffectiveMaxCents(
  availableBalanceInCents: number,
  platformMaxCents: number,
): number {
  return Math.min(availableBalanceInCents > 0 ? availableBalanceInCents : 0, platformMaxCents);
}

export function capAmountToMax(valueInCents: number, effectiveMaxCents: number): number {
  return Math.min(valueInCents, effectiveMaxCents);
}

/** Piso de US$ 50,00 exigido pelo produto para conversão de saldo referral em USD. */
export const REFERRAL_USD_CONVERT_MINIMUM_CENTS = 5000;

/**
 * Bloqueia conversão referral BRL→USD abaixo do piso de US$ 50, mesmo que o mínimo em
 * BRL (que valida o valor de origem, não o convertido) já tenha sido atingido. Escopado a
 * `withdrawalType === 'referral'` para não alterar o comportamento de Main/Partner.
 */
export function isBelowUsdConversionMinimum(
  withdrawalType: string | undefined,
  isConvertingToUsd: boolean,
  convertedAmountCents: number | null,
  minimumCents: number = REFERRAL_USD_CONVERT_MINIMUM_CENTS,
): boolean {
  return (
    withdrawalType === 'referral' &&
    isConvertingToUsd &&
    convertedAmountCents !== null &&
    convertedAmountCents < minimumCents
  );
}
